import type { FC } from "react";

import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Plus, X } from "lucide-react";

import type { OccupantFormValues } from "../hooks/use-occupant-form-schema";

import { ALERT_TYPES_ERROR_PATH } from "../hooks/use-occupant-form-schema";

import FormInputField from "./form-input-field";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { FormControl, FormField, FormItem } from "@/components/ui/form";

/** One labelled alert-type checkbox bound to a row of the phone/email array. */
const AlertCheckbox: FC<{ name: string; label: string }> = ({ name, label }) => (
  <FormField<OccupantFormValues>
    name={name as never}
    render={({ field }) => (
      <FormItem className="gap-0">
        <label className="flex cursor-pointer items-center gap-2">
          <FormControl>
            <Checkbox checked={field.value === true} onCheckedChange={field.onChange} />
          </FormControl>
          <span className="text-sm leading-none text-foreground">{label}</span>
        </label>
      </FormItem>
    )}
  />
);

/** Column headings above a destination list — kept in step with the rows below. */
const DestinationHeader: FC<{ valueLabel: string }> = ({ valueLabel }) => {
  const { t } = useTranslation(["occupants"]);

  return (
    <div className="hidden gap-3 sm:flex">
      <span className="w-[140px] text-sm font-medium leading-none text-foreground">
        {t("occupants:occupants-field-method-label", "Label")}
      </span>
      <span className="flex-1 text-sm font-medium leading-none text-foreground">{valueLabel}</span>
      <span className="w-[200px] text-sm font-medium leading-none text-foreground">
        {t("occupants:occupants-alert-types", "Alert types")}
      </span>
      {/* Matches the remove button's slot so the headings stay above their columns. */}
      <span className="w-9" />
    </div>
  );
};

/**
 * Every way to reach an occupant — the phone numbers and email addresses, each
 * with its own label and the alert types it carries — as editable rows that can
 * be added to and removed from.
 *
 * Editing a row is a real edit of that contact, so a number can be corrected
 * without leaving the drawer.
 */
const OccupantDestinationFields: FC = () => {
  const { t } = useTranslation(["occupants"]);
  const { control, formState } = useFormContext<OccupantFormValues>();
  // The alert-type rule belongs to no field, so its error sits under a key the
  // form values don't have.
  const alertTypesError = (formState.errors as Record<string, { message?: string } | undefined>)[
    ALERT_TYPES_ERROR_PATH
  ];

  const phones = useFieldArray<OccupantFormValues, "phones">({ control, name: "phones" });
  const emails = useFieldArray<OccupantFormValues, "emails">({ control, name: "emails" });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <DestinationHeader valueLabel={t("occupants:occupants-field-phone", "Phone number")} />

        {phones.fields.map((field, index) => (
          <div
            key={field.id}
            className="flex flex-col gap-2 rounded-[10px] border border-border p-3 sm:flex-row sm:items-start sm:gap-3 sm:rounded-none sm:border-0 sm:p-0"
          >
            <div className="w-full sm:w-[140px]">
              <FormInputField<OccupantFormValues>
                name={`phones.${index}.label`}
                placeholder={t("occupants:occupants-field-phone-label-placeholder", "Ex: Home")}
              />
            </div>

            <div className="w-full sm:flex-1">
              <FormInputField<OccupantFormValues>
                name={`phones.${index}.value`}
                placeholder={t("occupants:occupants-field-phone-placeholder", "Ex: 555-666-7777")}
              />
            </div>

            <div className="flex w-full flex-col gap-1.5 sm:w-[200px]">
              <AlertCheckbox
                label={t("occupants:occupants-alert-option-call", "Phone call")}
                name={`phones.${index}.alert_call`}
              />
              <AlertCheckbox
                label={t("occupants:occupants-alert-option-sms", "Text message (SMS)")}
                name={`phones.${index}.alert_sms`}
              />
            </div>

            {/* The last row stays put — the form always keeps somewhere to type. */}
            <Button
              aria-label={t("occupants:occupants-remove-phone", "Remove this phone number")}
              className="self-end text-muted-foreground hover:text-destructive sm:self-auto"
              disabled={phones.fields.length === 1}
              size="icon"
              type="button"
              variant="ghost"
              onClick={() => phones.remove(index)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}

        <Button
          className="w-fit gap-2 px-0 text-primary"
          size="sm"
          type="button"
          variant="link"
          onClick={() => phones.append({ label: "", value: "", alert_call: false, alert_sms: false })}
        >
          <Plus className="h-4 w-4" />
          {t("occupants:occupants-add-phone", "Add phone")}
        </Button>
      </div>

      <div className="flex flex-col gap-2">
        <DestinationHeader valueLabel={t("occupants:occupants-field-email", "Email")} />

        {emails.fields.map((field, index) => (
          <div
            key={field.id}
            className="flex flex-col gap-2 rounded-[10px] border border-border p-3 sm:flex-row sm:items-start sm:gap-3 sm:rounded-none sm:border-0 sm:p-0"
          >
            <div className="w-full sm:w-[140px]">
              <FormInputField<OccupantFormValues>
                name={`emails.${index}.label`}
                placeholder={t("occupants:occupants-field-email-label-placeholder", "Ex: Work")}
              />
            </div>

            <div className="w-full sm:flex-1">
              <FormInputField<OccupantFormValues>
                name={`emails.${index}.value`}
                placeholder={t("occupants:occupants-field-email-placeholder", "Ex: first.last@email.com")}
              />
            </div>

            <div className="flex w-full flex-col gap-1.5 sm:w-[200px]">
              <AlertCheckbox
                label={t("occupants:occupants-alert-option-email", "Email")}
                name={`emails.${index}.alert_email`}
              />
            </div>

            <Button
              aria-label={t("occupants:occupants-remove-email", "Remove this email address")}
              className="self-end text-muted-foreground hover:text-destructive sm:self-auto"
              disabled={emails.fields.length === 1}
              size="icon"
              type="button"
              variant="ghost"
              onClick={() => emails.remove(index)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}

        <Button
          className="w-fit gap-2 px-0 text-primary"
          size="sm"
          type="button"
          variant="link"
          onClick={() => emails.append({ label: "", value: "", alert_email: false })}
        >
          <Plus className="h-4 w-4" />
          {t("occupants:occupants-add-email", "Add email")}
        </Button>
      </div>

      {/* "At least one destination" and "at least one alert type" are about the
          whole section, so they are reported here rather than pinned to an
          arbitrary input — and reported at all, which is the point: a rule with
          nowhere to render just makes the submit button look broken. */}
      {alertTypesError?.message && <p className="text-sm font-medium text-destructive">{alertTypesError.message}</p>}
    </div>
  );
};

export default OccupantDestinationFields;
